import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core'
import { AbstractComponent, AuthenticationService } from 'lib-trend-core'
import { ThemeService } from '../../services/theme.service'

@Component({
    selector: 'header-component',
    templateUrl: './header.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class HeaderComponent
  extends AbstractComponent
  implements OnInit, OnDestroy {
  @ViewChild('userDropdownToggle') dropdownToggleRef!: ElementRef
  @ViewChild('userDropdownMenu') dropdownMenuRef!: ElementRef

  @Output() toggleSidenavEmitter = new EventEmitter()

  private clickListener: any
  isDropdownMenuOpened = false

  authenticationService = inject(AuthenticationService)
  renderer = inject(Renderer2)
  themeService = inject(ThemeService)

  currentUser!: any
  currentImage!: any

  @HostListener('document:fullscreenchange')
  onFullscreenChange(): void {
    const isFullscreen = !!document.fullscreenElement

    if (!isFullscreen) {
      this.removeFullScreenClass()
    }
  }

  @HostListener('document:click', ['$event'])
  onClickOutsideDropdownMenu(event: MouseEvent) {
    const target = event.target as HTMLElement
    const isInsideDropdown = target.closest('.dropdown-menu')

    if (!isInsideDropdown) {
      this.isDropdownMenuOpened = false
    }
  }

  ngOnInit(): void {
    this.setInitialThemeConfigs()
    this.listenToUserClickOutside()

    this.currentUser = this.getCurrentUserUser()
  }

  ngAfterViewInit(): void {
    this.toggleSidenav();
  }

  setInitialThemeConfigs() {
    this.themeService.initTheme();
  }

  override ngOnDestroy() {
    document.removeEventListener('click', this.clickListener)
  }

  listenToUserClickOutside() {
    this.clickListener = this.handleUserOutsideClick?.bind(this)
    document.addEventListener('click', this.clickListener)
  }

  triggerFullscreen(): void {
    const elem = document.documentElement as any

    if (elem.requestFullscreen) {
      elem.requestFullscreen()
      this.addFullScreenClass()
    } else if (elem.webkitRequestFullscreen) {
      elem.webkitRequestFullscreen()
      this.addFullScreenClass()
    } else if (elem.msRequestFullscreen) {
      elem.msRequestFullscreen()
      this.addFullScreenClass()
    }
  }

  addFullScreenClass() {
    document.body.classList.add('fullscreen-enable')
  }

  removeFullScreenClass() {
    document.body.classList.remove('fullscreen-enable')
  }

  toggleFullscreen(event: Event): void {
    event.preventDefault()
    event.stopPropagation()

    if (!document.fullscreenElement) {
      this.triggerFullscreen()
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen()
        this.removeFullScreenClass()
      } else if ((document as any).webkitExitFullscreen) {
        ; (document as any).webkitExitFullscreen()
        this.removeFullScreenClass()
      } else if ((document as any).msExitFullscreen) {
        ; (document as any).msExitFullscreen()
        this.removeFullScreenClass()
      }
    }
  }

  toggleColorTheme(): void {
    this.themeService.toggleTheme();
  }



  toggleUserDropdown(event: MouseEvent): void {
    event.preventDefault()
    event.stopPropagation()

    const toggleEl = this.dropdownToggleRef.nativeElement
    const menuEl = this.dropdownMenuRef.nativeElement

    const isOpen = toggleEl.classList.contains('show')

    if (isOpen) {
      toggleEl.classList.remove('show')
      menuEl.classList.remove('show')
    }

    toggleEl.classList.add('show')
    menuEl.classList.add('show')
  }

  toggleSidenav() {
    const width = window.innerWidth
    const sidenav = document.documentElement.getAttribute('data-sidenav-size')
    const newState = sidenav === 'default' ? 'condensed' : 'default'

    const html = document.documentElement
    const body = document.body

    const isSidebarEnabled = html.classList.contains('sidebar-enable')

    if (isSidebarEnabled) {
      html.classList.remove('sidebar-enable')
      body.style.overflow = ''

      const backdrop = document.getElementById('custom-backdrop')
      if (backdrop) backdrop.remove()
    } else {
      html.classList.add('menuitem-active', 'sidebar-enable')

      if (width < 768) {
        body.style.overflow = 'hidden'

        if (!document.getElementById('custom-backdrop')) {
          const backdrop = this.renderer.createElement('div')
          this.renderer.setAttribute(backdrop, 'id', 'custom-backdrop')
          this.renderer.addClass(backdrop, 'offcanvas-backdrop')
          this.renderer.addClass(backdrop, 'fade')
          this.renderer.addClass(backdrop, 'show')
          this.renderer.appendChild(body, backdrop)
        }
      }
    }

    if (width >= 768) {
      this.renderer.setAttribute(html, 'data-sidenav-size', newState)
    }

    this.toggleSidenavEmitter.emit()
  }

  toggleDropdownMenu(event: MouseEvent) {
    event.preventDefault()
    event.stopPropagation()
    this.isDropdownMenuOpened = !this.isDropdownMenuOpened
  }

  private handleUserOutsideClick(event: MouseEvent): void {
    if (
      !this.dropdownToggleRef?.nativeElement?.contains(event.target) &&
      !this.dropdownMenuRef?.nativeElement?.contains(event.target)
    ) {
      this.dropdownToggleRef?.nativeElement?.classList?.remove('show')
      this.dropdownMenuRef?.nativeElement?.classList?.remove('show')
    }
  }

  logout() {
    this.authenticationService.logout()
    window.location.href = '/login'
  }
}
